import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import {
  SearchOutlined,
  VisibilityOutlined,
  StorefrontOutlined,
  PersonOutline,
  AttachMoneyOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { ordersService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';

const Orders = () => {
  const { t, isRtl, dir } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await ordersService.getOrders({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        page: 1,
        pageSize: 50,
      });
      if (res) {
        setOrders(Array.isArray(res) ? res : (res.items || []));
      } else {
        setOrders([]);
      }
    } catch (e) {
      console.warn('Orders fetch error:', e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const filteredOrders = orders.filter((order) => {
    const id = (order.id || '').toString().toLowerCase();
    const client = (order.client || order.customerName || '').toLowerCase();
    const phone = (order.clientPhone || order.customerPhone || '');
    const restaurant = (order.restaurant || order.restaurantName || '').toLowerCase();
    const query = searchTerm.toLowerCase();

    return id.includes(query) || client.includes(query) || phone.includes(query) || restaurant.includes(query);
  });

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('ordersPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('ordersPage.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('ordersPage.searchOrders')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('filter')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">{t('all')}</MenuItem>
                <MenuItem value="PendingDispatch">{t('ordersPage.pending')}</MenuItem>
                <MenuItem value="Offered">{isRtl ? 'تم العرض للمناديب' : 'Offered'}</MenuItem>
                <MenuItem value="PickedUp">{t('ordersPage.pickedUp')}</MenuItem>
                <MenuItem value="Delivered">{t('ordersPage.delivered')}</MenuItem>
                <MenuItem value="FailedAttempt">{isRtl ? 'محاولة فاشلة' : 'Failed Attempt'}</MenuItem>
                <MenuItem value="ReturnedToRestaurant">{isRtl ? 'مرتجع للمطعم' : 'Returned'}</MenuItem>
                <MenuItem value="Cancelled">{t('ordersPage.cancelled')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Chip
                label={`${isRtl ? 'النتائج:' : 'Results:'} ${filteredOrders.length}`}
                sx={{ fontWeight: 700, height: 38, width: '100%', fontSize: '0.8rem', bgcolor: 'action.hover' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <LoadingState />
          ) : filteredOrders.length === 0 ? (
            <EmptyState message={t('noData')} subtitle={isRtl ? 'لم يتم العثور على طلبات مطابقة لمعايير البحث الحالية' : 'No orders matching the filter'} />
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{t('ordersPage.orderNumber')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 180 }}>{t('ordersPage.restaurant')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 220 }}>{t('ordersPage.customer')} / {t('address')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 130 }}>{t('ordersPage.orderTotal')}</TableCell>
                    <TableCell sx={{ textAlign: 'center !important', width: 130 }}>{t('status')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{t('ordersPage.courier')}</TableCell>
                    <TableCell sx={{ textAlign: 'center !important', width: 90 }}>{t('details')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem' }}>
                          #{order.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                          {order.createdAt || (isRtl ? 'الآن' : 'Now')}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <StorefrontOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {order.restaurant || order.restaurantName || (isRtl ? 'مطعم غير محدد' : 'Unknown Restaurant')}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {order.client || order.customerName || (isRtl ? 'عميل' : 'Customer')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.clientPhone || order.customerPhone || ''} • {order.address || order.deliveryAddress || ''}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem' }}>
                          {order.totalAmount || '0.00'} {t('currency')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                          {isRtl ? 'طعام' : 'Food'}: {order.foodAmount || 0} + {isRtl ? 'توصيل' : 'Fee'}: {order.deliveryFee || 0}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <StatusBadge status={order.status} size="small" />
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {order.courierName || (isRtl ? 'غير مسند' : 'Unassigned')}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
                          onClick={() => handleOpenDetails(order)}
                          sx={{ borderRadius: '6px', fontSize: '0.72rem', py: 0.3, px: 0.8 }}
                        >
                          {t('details')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
              {isRtl ? 'تفاصيل الطلب:' : 'Order Details:'} #{selectedOrder?.id}
            </Typography>
            {selectedOrder && <StatusBadge status={selectedOrder.status} />}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ my: 1 }}>
              {/* Top Info Grid */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ p: 1.5, height: '100%', textAlign: isRtl ? 'right' : 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5, color: 'primary.main' }}>
                      <StorefrontOutlined sx={{ fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {isRtl ? 'بيانات المطعم' : 'Restaurant Info'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedOrder.restaurant || selectedOrder.restaurantName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {isRtl ? 'الهاتف:' : 'Phone:'} {selectedOrder.restaurantPhone || 'N/A'}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ p: 1.5, height: '100%', textAlign: isRtl ? 'right' : 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5, color: 'primary.main' }}>
                      <PersonOutline sx={{ fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {isRtl ? 'بيانات العميل والعنوان' : 'Customer & Delivery Info'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedOrder.client || selectedOrder.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {selectedOrder.clientPhone || selectedOrder.customerPhone} • {selectedOrder.address || selectedOrder.deliveryAddress}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Financial Breakdown */}
              <Card variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', textAlign: isRtl ? 'right' : 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, color: 'secondary.main' }}>
                  <AttachMoneyOutlined sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {isRtl ? 'الحساب المالي للطلب' : 'Financial Breakdown'}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{isRtl ? 'صافي قيمة الطعام' : 'Food Amount'}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedOrder.foodAmount || 0} {t('currency')}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{t('ordersPage.deliveryFee')}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>{selectedOrder.deliveryFee || 0} {t('currency')}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{t('ordersPage.orderTotal')}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'success.main' }}>{selectedOrder.totalAmount || 0} {t('currency')}</Typography>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end' }}>
          <Button onClick={() => setDetailsOpen(false)} variant="contained" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Orders;
